// context/ProgramCategoryContext.jsx
"use client";
import React, { createContext, useContext, useCallback, useState } from "react";
import { BASE_URL } from "../config/config";

const ProgramCategoryContext = createContext();

export const ProgramCategoryProvider = ({ children }) => {
  const [programCategories, setProgramCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  // 🔹 FETCH
  const fetchProgramCategories = useCallback(async () => {
    if (!token) return;
    setLoading(true);

    try {
      const res = await fetch(`${BASE_URL}/program-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (res.ok && data.status === 200) {
        setProgramCategories(data.data || []);
        setError("");
      } else {
        setError(data.message || "Fetch failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔹 ADD
  const addProgramCategory = async (payload) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      payload.create_audit_id = user.id;

      const res = await fetch(`${BASE_URL}/program-categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok && data.status === 201) {
        fetchProgramCategories();
        return true;
      }
      alert(data.message || "Add failed");
      return false;
    } catch {
      alert("Network error");
      return false;
    }
  };

  // 🔹 UPDATE
  const updateProgramCategory = async (id, payload) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      payload.update_audit_id = user.id;

      const res = await fetch(`${BASE_URL}/program-categories/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok && data.status === 200) {
        fetchProgramCategories();
        return true;
      }
      alert(data.message || "Update failed");
    } catch {
      alert("Network error");
    }
  };

  // 🔹 DELETE
  const deleteProgramCategory = async (id) => {
    if (!window.confirm("Delete this program category?")) return;

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${BASE_URL}/program-categories/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id: user.id }),
      });

      const data = await res.json();
      if (res.ok && data.status === 200) {
        setProgramCategories((prev) => prev.filter((p) => p.id !== id));
      } else {
        alert(data.message || "Delete failed");
      }
    } catch {
      alert("Delete failed");
    }
  };

  // 🔹 STATUS
  const toggleProgramCategoryStatus = async (id, status) => {
    const newStatus = status === "Active" ? "Inactive" : "Active";

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${BASE_URL}/program-categories/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status: newStatus,
          update_audit_id: user.id,
        }),
      });

      const data = await res.json();
      if (res.ok && data.status === 200) {
        setProgramCategories((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, status: newStatus } : p
          )
        );
      }
    } catch {
      alert("Status update failed");
    }
  };

  return (
    <ProgramCategoryContext.Provider
      value={{
        programCategories,
        loading,
        error,
        fetchProgramCategories,
        addProgramCategory,
        updateProgramCategory,
        deleteProgramCategory,
        toggleProgramCategoryStatus,
      }}
    >
      {children}
    </ProgramCategoryContext.Provider>
  );
};

export const useProgramCategories = () =>
  useContext(ProgramCategoryContext);
